import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Input,
  InputNumber,
  message,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  checkRefundIsManual,
  decideRefundsWithDetails,
  getRefundsWithDetails,
} from "../../apis/po.api";
import { RefundTransactions } from "../../components/po/refund_transactions";
import { usePermissions } from "../../custom_hooks/permission/permission_hook";

// - Item damaged
// - Wrong item sent
// - Sales / product choice mistake
// - Personal preferance
// - Others - give an option to add remark

export const ApprovalReasons = [
  "Item damaged",
  "Wrong item sent",
  "Sales / product choice mistake",
  "Personal preference",
  "Sample Return",
  "High Lead Time",
  "Discontinued",
  "Others",
];

export default function RefundsV2() {
  const { hasPermission } = usePermissions();
  const [created_at__gte, setCreatedAtGte] = useState("");
  const [created_at__lte, setCreatedAtLte] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [refundForApproval, setRefundForApproval] = useState<number | null>(
    null
  );
  const [status, setStatus] = useState<"pending" | "declined" | "initiated">(
    "pending"
  );
  const [approvalReason, setApprovalReason] = useState<string | null>(null);
  const [approvalRemark, setApprovalRemark] = useState<string>();
  const [showApprovalRemark, setShowApprovalRemark] = useState(false);
  const [applyBifurcation, setApplyBifurcation] = useState(false);
  const [itemBifurcationCharge, setItemBifurcationCharge] = useState<
    "vendor" | "client" | "mdepot" | null
  >(null);
  const [itemBifurcationChargeValue, setItemBifurcationChargeValue] = useState<
    number | null
  >(null);
  const [refundIsManualPayment, setRefundIsManualPayment] = useState(false);
  const [manualRefundReferenceId, setManualRefundReferenceId] =
    useState<string>();
  const [manualRefundPaymentId, setManualRefundPaymentId] = useState<string>();

  const [raiseDelivery, setRaiseDelivery] = useState(false);
  const [dispatchTime, setDispatchTime] = useState<string | null>(null);

  const [logActiveRefund, setLogActiveRefund] = useState<number | null>(null);

  useEffect(() => {
    if (refundForApproval) {
      checkRefundIsManual(refundForApproval)
        .then((res: any) => {
          setRefundIsManualPayment(res?.is_manual);
        })
        .catch((err) => {
          setRefundIsManualPayment(false);
          setManualRefundReferenceId(undefined);
          setManualRefundPaymentId(undefined);
        });
    } else {
      setRefundIsManualPayment(false);
      setManualRefundReferenceId(undefined);
      setManualRefundPaymentId(undefined);
    }
  }, [refundForApproval]);

  useEffect(() => {
    if (approvalReason === "Others") {
      setShowApprovalRemark(true);
    } else {
      setShowApprovalRemark(false);
    }
  }, [approvalReason]);

  useEffect(() => {
    setLoading(true);
    const params: Record<string, any> = { page, status };
    if (created_at__gte) params.created_at__gte = created_at__gte;
    if (created_at__lte) params.created_at__lte = created_at__lte;
    getRefundsWithDetails(params)
      .then((res: any) => {
        setCount(res?.count);
        setData(res?.results);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [created_at__gte, created_at__lte, page, status]);

  const handleApproval = useCallback(
    async (decision: "approve" | "reject") => {
      // if (decision === "approve" && !approvalReason) {
      //   message.error("Please select a reason for approval");
      //   return;
      // }
      // if (decision === "approve" && showApprovalRemark && !approvalRemark) {
      //   message.error("Please enter a remark");
      //   return;
      // }

      try {
        if (!refundForApproval) {
          return;
        }
        setLoading(true);
        const payload = {
          refund_id: refundForApproval,
          decision: decision,
          // reason: showApprovalRemark ? approvalRemark : approvalReason,
        } as Record<string, any>;

        if (raiseDelivery) {
          payload["dispatch_time"] = dispatchTime;
        }
        if (applyBifurcation) {
          if (!itemBifurcationCharge) {
            message.error("Please select bifurcation type");
            return;
          }
          if (!itemBifurcationChargeValue) {
            message.error("Please enter bifurcation amount");
            return;
          }

          payload["bifurcation_responsibility"] = itemBifurcationCharge;
          payload["bifurcation_amount"] = itemBifurcationChargeValue;
        }

        if (refundIsManualPayment) {
          if (!manualRefundReferenceId) {
            message.error("Please enter bank account number");
            return;
          }
          if (!manualRefundPaymentId) {
            message.error("Please enter IFSC code");
            return;
          }
          payload["manual_reference_id"] = manualRefundReferenceId;
          payload["manual_payment_id"] = manualRefundPaymentId;
        }

        const response = await decideRefundsWithDetails(payload);
      } catch (error) {
        message.error("Error approving refund");
      } finally {
        setLoading(false);
        setRefundForApproval(null);

        // Refresh — keep the active status + date filters
        const refreshParams: Record<string, any> = { page, status };
        if (created_at__gte) refreshParams.created_at__gte = created_at__gte;
        if (created_at__lte) refreshParams.created_at__lte = created_at__lte;
        getRefundsWithDetails(refreshParams)
          .then((res: any) => {
            setCount(res?.count);
            setData(res?.results);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [
      applyBifurcation,
      dispatchTime,
      itemBifurcationCharge,
      itemBifurcationChargeValue,
      manualRefundPaymentId,
      manualRefundReferenceId,
      page,
      status,
      created_at__gte,
      created_at__lte,
      raiseDelivery,
      refundForApproval,
      refundIsManualPayment,
    ]
  );

  const columns = useMemo(() => {
    return [
      {
        title: "Lead ID",
        key: "lead_id",
        render: (record: any) => {
          return record?.estimate?.lead_id;
        },
      },
      {
        title: "PO Number",
        key: "po_number",
        render: (record: any) => {
          return record?.po?.po_number;
        },
      },
      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        render: (text: any) => dayjs(text).format("YYYY-MM-DD HH:mm"),
      },
      {
        title: "Total Refund Amount",
        key: "total_refund_amount",
        render: (record: any) => {
          return <div>Rs. {record?.total_refund}</div>;
        },
      },
      {
        title: "Remarks",
        key: "remarks",
        width: 200,
        render: (record: any) => {
          return (
            <Tooltip title={record?.po?.remark}>
              <div
                style={{
                  maxWidth: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {record?.po?.remark}
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: "Attachments",
        key: "attachments",
        render: (record: any) => {
          const attachments = record?.po_attachments;
          if (attachments?.length === 0) {
            return <div>No attachments</div>;
          }

          return (
            <div>
              {attachments?.map((attachment: any, index: number) => {
                return (
                  <a
                    key={attachment?.id}
                    href={attachment?.attachment_link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Tag color="purple">File {index + 1}</Tag>
                  </a>
                );
              })}
            </div>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        render: (record: any) => {
          if (record?.status === "initiated" || record?.status === "declined") {
            return (
              <Space>
                <Link to={`/po-details/${record?.po?.id}`}>
                  <Button size="small">View details</Button>
                </Link>
                <Button
                  size="small"
                  onClick={() => {
                    setLogActiveRefund(record?.id);
                  }}
                >
                  View Transaction Log
                </Button>
              </Space>
            );
          }

          return (
            <Space>
              {hasPermission("refund.approval") && (
                <Button
                  type="primary"
                  className="th-font-size-14 th-font-weight-500"
                  onClick={() => {
                    setRefundForApproval(record?.id);
                  }}
                  size="small"
                >
                  Process refund
                </Button>
              )}
              <Link to={`/po-details/${record?.po?.id}`}>
                <Button size="small">View details</Button>
              </Link>
            </Space>
          );
        },
      },
    ];
  }, [hasPermission]);

  return (
    <>
      <div className="mx-3 my-3 px-3 py-2 white-color-bg">
        <div className="d-flex justify-content-between">
          <h3>Refunds</h3>
          <Space>
            <DatePicker.RangePicker
              onChange={(date, dateString) => {
                // On clear, set gte to empty string, and lte to empty string
                if (!date) {
                  setCreatedAtGte("");
                  setCreatedAtLte("");
                  return;
                }
                // Set gte to that date, and lte a day after
                setCreatedAtGte(dateString[0]);
                const next = dayjs(dateString[1])
                  .add(1, "day")
                  .format("YYYY-MM-DD");
                setCreatedAtLte(next);
              }}
              allowClear
            />
          </Space>
        </div>
        <Radio.Group
          onChange={(e) => setStatus(e.target.value)}
          value={status}
          className="mt-3"
        >
          <Radio.Button value="pending">Pending</Radio.Button>
          <Radio.Button value="initiated">Approved</Radio.Button>
          <Radio.Button value="declined">Declined</Radio.Button>
        </Radio.Group>
        <Table
          className="mt-3"
          loading={loading}
          dataSource={data}
          pagination={{
            pageSize: 10,
            total: count,
            onChange: (page) => {
              setPage(page);
            },
          }}
          columns={columns}
          rowKey={(record) => record.id}
        />
      </div>

      <Drawer
        title="Approve Refund"
        placement="right"
        onClose={() => setRefundForApproval(null)}
        open={refundForApproval !== null}
        width={400}
        extra={
          <Space>
            <Button
              onClick={() => setRefundForApproval(null)}
              className="th-font-size-14 th-font-weight-500"
            >
              Cancel
            </Button>
          </Space>
        }
      >
        <div className="">
          <p>
            Are you sure you want to approve the refund request for{" "}
            {data?.find((item) => item.id === refundForApproval)?.po?.po_number}
            ?
          </p>
          <div className="my-3">
            <div className="d-flex align-items-center mb-3 gap-2">
              <Checkbox
                checked={applyBifurcation}
                onChange={(e) => {
                  setApplyBifurcation(e.target.checked);
                }}
              />
              <Typography.Title level={5} className="mb-0">
                Loss Bifurcation (if any)
              </Typography.Title>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <Select
                  value={itemBifurcationCharge}
                  onChange={(value) => {
                    setItemBifurcationCharge(value);
                  }}
                  style={{ width: 150, marginRight: "1rem" }}
                  placeholder="Select Type"
                  disabled={!applyBifurcation}
                  options={[
                    { label: "Vendor", value: "vendor" },
                    { label: "Client", value: "client" },
                    { label: "Material Depot", value: "mdepot" },
                  ]}
                />
              </div>
              <div>
                <InputNumber
                  min={0}
                  value={itemBifurcationChargeValue}
                  onChange={(value) => {
                    setItemBifurcationChargeValue(value);
                  }}
                  style={{ width: 150 }}
                  placeholder="Enter Amount"
                  disabled={!applyBifurcation}
                />
              </div>
            </div>
          </div>

          {refundIsManualPayment && (
            <div className="my-3">
              <Input
                placeholder="Enter Bank Account Number"
                value={manualRefundReferenceId}
                onChange={(e) => setManualRefundReferenceId(e.target.value)}
                className="mb-2"
              />
              <Input
                placeholder="Enter IFSC Code"
                value={manualRefundPaymentId}
                onChange={(e) => setManualRefundPaymentId(e.target.value)}
                className="mb-2"
              />
            </div>
          )}

          {/* <div className="my-3">
            <Select
              className="w-100"
              placeholder="Select reason for approval"
              onChange={(value) => {
                setApprovalReason(value);
              }}
              value={approvalReason}
            >
              {ApprovalReasons.map((reason) => {
                return (
                  <Select.Option key={reason} value={reason}>
                    {reason}
                  </Select.Option>
                );
              })}
            </Select>
            {showApprovalRemark && (
              <div className="mt-2">
                <Input.TextArea
                  placeholder="Enter remark"
                  onChange={(e) => {
                    setApprovalRemark(e.target.value);
                  }}
                  value={approvalRemark}
                  rows={4}
                />
              </div>
            )}
          </div> */}
          <Space>
            <Button
              type="primary"
              className="th-font-size-14 th-font-weight-500"
              onClick={() => {
                handleApproval("approve");
              }}
            >
              Confirm
            </Button>
            <Button
              className="th-font-size-14 th-font-weight-500"
              onClick={() => {
                handleApproval("reject");
              }}
              danger
            >
              Reject
            </Button>
          </Space>
        </div>
      </Drawer>
      <Drawer
        title="Refund Transactions"
        placement="right"
        width={600}
        onClose={() => setLogActiveRefund(null)}
        open={logActiveRefund !== null}
      >
        <RefundTransactions refundId={logActiveRefund} />
      </Drawer>
    </>
  );
}
